import type { Bible, ContinuityIssue, Moment } from "./types";
import { getCharacter, getMoment, isAtOrBefore, resolveCharacterState, sortedMoments } from "./engine";

function nowIso(): string {
  return new Date().toISOString();
}

function issue(
  partial: Omit<ContinuityIssue, "createdAt" | "updatedAt" | "sources"> & { sources?: ContinuityIssue["sources"] },
): ContinuityIssue {
  return {
    sources: partial.sources ?? [{ label: "Contrôle automatique" }],
    createdAt: nowIso(),
    updatedAt: nowIso(),
    ...partial,
  };
}

export function detectContinuityIssues(bible: Bible): ContinuityIssue[] {
  const found: ContinuityIssue[] = [];
  const momentIndex = new Map(bible.moments.map((m) => [m.id, m]));

  for (const character of bible.characters) {
    for (const patch of character.states) {
      if (!momentIndex.has(patch.validFromMomentId)) {
        found.push(
          issue({
            id: `auto-missing-moment-${patch.id}`,
            status: "verify",
            severity: "critical",
            state: "open",
            title: `Moment inexistant pour ${character.name}`,
            description: `Le changement ${patch.id} référence ${patch.validFromMomentId}, absent de la Bible.`,
            entityRefs: [character.id, patch.id, patch.validFromMomentId],
            momentIds: [patch.validFromMomentId],
          }),
        );
      }
      if (patch.status === "canon" && (!patch.sources || patch.sources.length === 0)) {
        found.push(
          issue({
            id: `auto-canon-no-source-${patch.id}`,
            status: "verify",
            severity: "important",
            state: "open",
            title: `Canon sans source (${character.name})`,
            description: `Le changement ${patch.id} est marqué canon sans source.`,
            entityRefs: [character.id, patch.id],
            momentIds: [patch.validFromMomentId],
          }),
        );
      }
    }
  }

  const sampleMoments = representativeMoments(bible);
  for (const moment of sampleMoments) {
    const ranks = new Map<number, string[]>();
    for (const character of bible.characters) {
      const state = resolveCharacterState(bible, character.id, moment.id);
      if (state?.rank != null) {
        const list = ranks.get(state.rank) ?? [];
        list.push(character.id);
        ranks.set(state.rank, list);
      }
    }
    for (const [rank, ids] of ranks) {
      if (ids.length > 1) {
        found.push(
          issue({
            id: `auto-dup-rank-${moment.id}-${rank}`,
            status: "verify",
            severity: "attention",
            state: "open",
            title: `Rang ${rank} dupliqué à ${moment.label}`,
            description: `Plusieurs personnages partagent le rang ${rank} dans la branche ${moment.branchId} : ${ids.join(", ")}.`,
            entityRefs: ids,
            momentIds: [moment.id],
          }),
        );
      }
    }
  }

  for (const character of bible.characters) {
    const byBranch = new Map<string, typeof character.states>();
    for (const patch of character.states) {
      const key = `${patch.branchId}::${patch.loopIterationId ?? ""}`;
      const list = byBranch.get(key) ?? [];
      list.push(patch);
      byBranch.set(key, list);
    }
    for (const [key, patches] of byBranch) {
      const ordered = [...patches].sort((a, b) =>
        a.validFromMomentId.localeCompare(b.validFromMomentId),
      );
      let seenDead = false;
      let deadFrom: string | undefined;
      for (const patch of ordered) {
        if (patch.lifeStatus === "dead") {
          seenDead = true;
          deadFrom = patch.validFromMomentId;
        } else if (
          seenDead &&
          (patch.lifeStatus === "alive" || patch.lifeStatus === "injured")
        ) {
          found.push(
            issue({
              id: `auto-dead-then-alive-${character.id}-${key}`,
              status: "verify",
              severity: "critical",
              state: "open",
              title: `${character.name} redevient actif après une mort, même branche`,
              description: `Mort à ${deadFrom}, puis ${patch.lifeStatus} à ${patch.validFromMomentId} sur ${key}. Vérifier s’il s’agit d’une autre itération (à brancher) plutôt que d’une résurrection linéaire.`,
              entityRefs: [character.id],
              momentIds: [deadFrom!, patch.validFromMomentId],
            }),
          );
        }
      }
    }

    for (const patch of character.states) {
      const phys = (patch.physicalState || "").toLowerCase();
      if (
        patch.lifeStatus === "injured" &&
        (phys.includes("intact") || phys.includes("ni blessé"))
      ) {
        found.push(
          issue({
            id: `auto-state-conflict-${patch.id}`,
            status: "verify",
            severity: "attention",
            state: "open",
            title: `États physiques contradictoires (${character.name})`,
            description: `lifeStatus=${patch.lifeStatus} mais physicalState=« ${patch.physicalState} » (${patch.id}). Non corrigé automatiquement.`,
            entityRefs: [character.id, patch.id],
            momentIds: [patch.validFromMomentId],
          }),
        );
      }
    }
  }

  for (const secret of bible.secrets) {
    if (
      secret.revealedToReaderAtMomentId &&
      getMoment(bible, secret.trueFromMomentId) &&
      getMoment(bible, secret.revealedToReaderAtMomentId) &&
      !isAtOrBefore(bible, secret.trueFromMomentId, secret.revealedToReaderAtMomentId)
    ) {
      found.push(
        issue({
          id: `auto-reveal-before-true-${secret.id}`,
          status: "verify",
          severity: "important",
          state: "open",
          title: `Révélation antérieure à l’existence du secret`,
          description: `${secret.title} : révélation lecteur ${secret.revealedToReaderAtMomentId} avant vérité ${secret.trueFromMomentId}.`,
          entityRefs: [secret.id],
          momentIds: [secret.trueFromMomentId, secret.revealedToReaderAtMomentId],
        }),
      );
    }
  }

  const magda = getCharacter(bible, "magda");
  if (magda?.gift) {
    const g = magda.gift.toLowerCase();
    const inventsLimit =
      (g.includes("limite") && !g.includes("aucune limite") && !g.includes("pas de limite")) ||
      g.includes("maximum de copie") ||
      g.includes("ne peut copier qu");
    if (inventsLimit) {
      found.push(
        issue({
          id: "auto-magda-limit",
          status: "contradiction",
          severity: "important",
          state: "open",
          title: "Formulation pouvant inventer une limite au Don de Magda",
          description: magda.gift,
          entityRefs: ["magda"],
        }),
      );
    }
  }

  const bjorn = getCharacter(bible, "bjorn");
  if (bjorn && /guerrier/i.test(bjorn.className) && !/sorcier/i.test(bjorn.className)) {
    found.push(
      issue({
        id: "auto-bjorn-warrior",
        status: "contradiction",
        severity: "important",
        state: "open",
        title: "Björn n’est pas un Guerrier",
        description: `Classe actuelle « ${bjorn.className} ». Le canon retient Sorcier spécialisé Foudre.`,
        entityRefs: ["bjorn"],
      }),
    );
  }

  return found;
}

function representativeMoments(bible: Bible): Moment[] {
  const wanted = ["t3-c1", "t3-c12", "t3-c18", "t3-c19", "t3-c19-loop-b", "t3-c20", "t1-c1"];
  const list = wanted.map((id) => getMoment(bible, id)).filter((m): m is Moment => !!m);
  if (list.length) return list;
  return sortedMoments(bible, "story").slice(0, 8);
}

export function mergeDetectedIssues(bible: Bible): ContinuityIssue[] {
  const auto = detectContinuityIssues(bible);
  const existingIds = new Set(bible.continuityIssues.map((i) => i.id));
  return [...bible.continuityIssues, ...auto.filter((i) => !existingIds.has(i.id))];
}
